import React, { useState, useEffect, createContext, useContext } from 'react';
import Joyride, { Step, CallBackProps, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import {
  Box,
  Typography,
  useTheme,
  IconButton,
} from '@mui/material';
import {
  Help as HelpIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const JoyrideComponent = Joyride as any;

// The new structure for tutorial steps, compatible with react-joyride
interface TutorialStep extends Step {
  navigateTo?: string;
}

interface TutorialContextType {
  activeTutorial: string | null;
  startTutorial: (type: string) => void;
  completeTutorial: () => void;
  isFirstTimeUser: () => boolean;
}

const TutorialContext = createContext<TutorialContextType | null>(null);

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

// Tutorial steps definition, now in react-joyride format.
// All steps use target: 'body' with placement: 'center' to avoid
// broken element targets on mobile or when elements don't exist.
const tutorialSteps: Record<string, TutorialStep[]> = {
  firstTime: [
    {
      target: 'body',
      placement: 'center',
      title: 'Welcome to FuelTron',
      content: 'Your food, labs, sleep and training in one place. Here is a quick tour.',
      disableBeacon: true,
    },
    {
      target: 'body',
      placement: 'center',
      title: 'Your dashboard',
      content: 'Start here each day. Your FuelTron Score covers nutrition, health and consistency. Tap it to see each part.',
      disableBeacon: true,
    },
    {
      target: 'body',
      placement: 'center',
      title: 'Logging food',
      content: 'Type what you ate, take a photo, or say it out loud. FuelTron works out the nutrition.',
      disableBeacon: true,
    },
    {
      target: 'body',
      placement: 'center',
      title: 'My Foods',
      content: 'Save the meals you eat often and log them in one tap. You will find it on the Food Log page.',
      disableBeacon: true,
    },
    {
      target: 'body',
      placement: 'center',
      title: 'Supplements and bloodwork',
      content: 'Check off your supplements each day and upload your bloodwork. Both feed your FuelTron Health Score.',
      disableBeacon: true,
    },
    {
      target: 'body',
      placement: 'center',
      title: 'Your coach',
      content: 'The coach reads your food, sleep and training together and tells you what it sees. Past notes and weekly recaps are on the Coach tab.',
      disableBeacon: true,
    },
    {
      target: 'body',
      placement: 'center',
      title: 'Ask anything',
      content: 'Tap the bar at the bottom of any page to ask a question, log food by voice, or get nutrition advice.',
      disableBeacon: true,
    },
    {
      target: 'body',
      placement: 'center',
      title: 'You\'re ready',
      content: 'Log your first meal. The more you log, the more FuelTron can tell you. Replay this tour any time from Profile settings.',
      disableBeacon: true,
    },
  ],
  tierBenefits: [
    {
      target: 'body',
      placement: 'center',
      title: 'Free and Pro',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>Free</Typography>
          <Typography variant="body2" component="div" sx={{ mb: 2 }}>
            - Food, supplement and workout logging<br/>
            - A daily allowance of food lookups and questions<br/>
            - Your FuelTron Score
          </Typography>
          <Typography variant="h6" gutterBottom>Pro adds</Typography>
          <Typography variant="body2" component="div">
            - <strong>No daily limits</strong><br/>
            - <strong>Bloodwork analysis</strong><br/>
            - <strong>The full coach</strong><br/>
            - <strong>Priority support</strong>
          </Typography>
        </Box>
      ),
      disableBeacon: true,
    },
  ],
};

interface TutorialSystemProps {
  children: React.ReactNode;
}

export const TutorialProvider: React.FC<TutorialSystemProps> = ({ children }) => {
  const [activeTutorial, setActiveTutorial] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [run, setRun] = useState(false);
  const { user, isNewUser, clearNewUserFlag } = useAuth();
  
  const startTutorial = React.useCallback((type: string) => {
    const steps = tutorialSteps[type];
    if (!steps) {
      console.warn(`Tutorial type '${type}' not found`);
      return;
    }

    setTimeout(() => {
      setActiveTutorial(type);
      setStepIndex(0);
      setRun(true);
    }, 100);
  }, []);

  // Tutorial auto-start disabled — replaced by welcome screen + contextual prompts.
  // Manual replay is still available from Profile Settings.

  const completeTutorial = () => {
    if (activeTutorial === 'firstTime' && isNewUser) {
      clearNewUserFlag();
      localStorage.setItem('tutorialCompleted', 'true');
    }
    setActiveTutorial(null);
    setRun(false);
    setStepIndex(0);
  };

  const isFirstTimeUser = () => {
    return !localStorage.getItem('tutorialCompleted');
  };

  // Core logic for handling react-joyride events
  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data;

    if (type === EVENTS.STEP_AFTER) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      setStepIndex(nextStepIndex);
    } else if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      completeTutorial();
    }
  };

  const currentSteps = activeTutorial ? tutorialSteps[activeTutorial] : [];

  return (
    <TutorialContext.Provider
      value={{
        activeTutorial,
        startTutorial,
        completeTutorial,
        isFirstTimeUser,
      }}
    >
      {children}
      {activeTutorial && currentSteps.length > 0 && (
      <JoyrideComponent
        steps={currentSteps}
        run={run}
        stepIndex={stepIndex}
        callback={handleJoyrideCallback}
        continuous
        showProgress
        showSkipButton
        disableScrollParentFix
        styles={{
          options: {
            arrowColor: '#1a1b26',
            backgroundColor: '#1a1b26',
            primaryColor: '#00e0ff',
            textColor: '#f5f5f5',
            zIndex: 1300,
          },
          tooltip: {
            background: '#1a1b26',
            border: '1.5px solid rgba(0,224,255,0.2)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            padding: '16px',
          },
          buttonNext: {
            backgroundColor: '#00e0ff',
            color: '#000000',
            fontWeight: 600,
            borderRadius: '12px',
            padding: '8px 16px',
            textTransform: 'uppercase',
            fontSize: '0.9rem',
            lineHeight: 1.75,
          },
          buttonBack: {
            color: 'rgba(255,255,255,0.7)',
            padding: '8px 16px',
          },
          buttonSkip: {
            color: 'rgba(255,255,255,0.7)',
            fontSize: '0.9rem',
          }
        }}
      />
      )}
    </TutorialContext.Provider>
  );
};

// Updated help button component - No longer part of this file
export const TutorialHelpButton: React.FC<{
  tutorialType: string;
  position?: { bottom?: number; right?: number };
}> = ({ tutorialType, position = { bottom: 80, right: 24 } }) => {
  const { startTutorial } = useTutorial();
  const theme = useTheme();

  return (
    <IconButton
      onClick={() => startTutorial(tutorialType)}
      sx={{
        position: 'fixed',
        bottom: position.bottom,
        right: position.right,
        zIndex: theme.zIndex.fab,
        bgcolor: '#00E0FF',
        color: 'primary.contrastText',
        '&:hover': {
          bgcolor: 'primary.dark',
        },
        boxShadow: theme.shadows[6],
      }}
    >
      <HelpIcon />
    </IconButton>
  );
}; 